'use client';
import React, { useState } from 'react';
import { PlayCircle, FileText, HelpCircle, Lock, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const lessons = [
  { id: 'les-001', title: 'Welcome & Course Overview', type: 'video', duration: '5:42', free: true, completed: true },
  { id: 'les-002', title: 'What is Machine Learning?', type: 'video', duration: '18:20', free: true, completed: true },
  { id: 'les-003', title: 'Setting Up Your Python Environment', type: 'reading', duration: '12 min', free: true, completed: true },
  { id: 'les-004', title: 'Supervised vs Unsupervised Learning', type: 'video', duration: '22:10', free: false, completed: false },
  { id: 'les-005', title: 'Your First ML Model: Linear Regression', type: 'video', duration: '34:55', free: false, completed: false },
  { id: 'les-006', title: 'Quiz: Core ML Concepts', type: 'quiz', duration: '10 min', free: false, completed: false },
  { id: 'les-007', title: 'Classification with Logistic Regression', type: 'video', duration: '28:40', free: false, completed: false },
  { id: 'les-008', title: 'Decision Trees & Random Forests', type: 'video', duration: '41:12', free: false, completed: false },
  { id: 'les-009', title: 'Feature Engineering Deep Dive', type: 'video', duration: '35:28', free: false, completed: false },
  { id: 'les-010', title: 'Model Evaluation & Cross-Validation', type: 'video', duration: '29:05', free: false, completed: false },
  { id: 'les-011', title: 'Hyperparameter Tuning with GridSearchCV', type: 'video', duration: '24:33', free: false, completed: false },
  { id: 'les-012', title: 'Quiz: Model Selection & Evaluation', type: 'quiz', duration: '15 min', free: false, completed: false },
  { id: 'les-013', title: 'Intro to Neural Networks', type: 'video', duration: '38:50', free: false, completed: false },
  { id: 'les-014', title: 'Unsupervised Learning: K-Means Clustering', type: 'video', duration: '27:15', free: false, completed: false },
  { id: 'les-015', title: 'Dimensionality Reduction with PCA', type: 'video', duration: '22:40', free: false, completed: false },
  { id: 'les-016', title: 'Capstone Project: End-to-End ML Pipeline', type: 'reading', duration: '20 min', free: false, completed: false },
  { id: 'les-017', title: 'Deploying Your Model as a REST API', type: 'video', duration: '31:22', free: false, completed: false },
  { id: 'les-018', title: 'Course Wrap-Up & Next Steps', type: 'video', duration: '8:14', free: false, completed: false },
];

const typeIcon = (type: string) => {
  if (type === 'video') return <PlayCircle size={14} className="text-primary" />;
  if (type === 'reading') return <FileText size={14} className="text-blue-500" />;
  if (type === 'quiz') return <HelpCircle size={14} className="text-amber-500" />;
  return <PlayCircle size={14} />;
};

export default function CourseCurriculum() {
  const [expanded, setExpanded] = useState(true);

  const completed = lessons.filter((l) => l.completed).length;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold text-foreground">Course Curriculum</h2>
          <span className="text-xs text-muted-foreground">
            {lessons.length} lessons · {completed} completed
          </span>
        </div>
        {expanded ? <ChevronUp size={18} className="text-muted-foreground" /> : <ChevronDown size={18} className="text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="border-t border-border divide-y divide-border fade-in">
          {lessons.map((lesson) => (
            <div
              key={lesson.id}
              className={`flex items-center gap-3 px-5 py-3 hover:bg-muted/40 transition-colors ${lesson.completed ? 'bg-success/5' : ''}`}
            >
              <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                {lesson.completed ? (
                  <CheckCircle size={16} className="text-success" />
                ) : lesson.free ? (
                  typeIcon(lesson.type)
                ) : (
                  <Lock size={13} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm leading-snug ${lesson.completed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                  {lesson.title}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {lesson.free && (
                  <span className="text-xs font-semibold text-primary bg-secondary px-2 py-0.5 rounded-full">Free</span>
                )}
                <span className="text-xs text-muted-foreground font-tabular">{lesson.duration}</span>
                <span className="text-muted-foreground">{typeIcon(lesson.type)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}